import {useRepoInfo} from '@github-ui/agent-sessions/hooks/use-repo-info'
import {getSortedResourceIds} from '@github-ui/agent-sessions/hooks/usePullRequests'
import {fetchTasks, type FetchTasksResponse} from '@github-ui/agent-sessions/hooks/useTasks'
import type {IdToPullRequestMap, Pull} from '@github-ui/agent-sessions/types/pull'
import type {BaseTask} from '@github-ui/agent-sessions/types/task'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useMemo} from 'react'

import {normalizeTaskUrl, taskUrlKeys} from '../utils/task-url'

const STATE_SYNC_PAGE_SIZE = 100
const STATE_SYNC_QUERY_KEY = ['copilot-immersive-tasks-state-sync', 1, STATE_SYNC_PAGE_SIZE] as const

export interface TaskStateEntry {
  state: string
  title?: string
}

export type TaskStateLookup = ReadonlyMap<string, TaskStateEntry>

const EMPTY_LOOKUP: TaskStateLookup = new Map()

export function lookupTaskState(lookup: TaskStateLookup, url: string): TaskStateEntry | undefined {
  return lookup.get(normalizeTaskUrl(url))
}

export interface UseTaskStateSyncOptions {
  /** When `false`, skip polling and return an empty lookup. */
  enabled?: boolean
}

function resolvePull(task: BaseTask, pullRequestsMap: IdToPullRequestMap | undefined): Pull | undefined {
  return task.pull ?? (task.resource_id ? pullRequestsMap?.[task.resource_id] : undefined)
}

/**
 * Live agent task state for surfaces outside the TaskList sidebar. Polls a single combined
 * active + archived list every 20s so PR-close (which archives the task) still updates the
 * icon. The poll uses a private cache key (see {@link STATE_SYNC_QUERY_KEY}) with a larger page
 * size than the sidebar so other consumers can't shrink our result set. Read entries via
 * {@link lookupTaskState}.
 */
export function useTaskStateSync({enabled = true}: UseTaskStateSyncOptions = {}): TaskStateLookup {
  const queryClient = useQueryClient()
  const {data} = useQuery<FetchTasksResponse>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: STATE_SYNC_QUERY_KEY,
    queryFn: () =>
      fetchTasks({
        queryClient,
        queryParams: {page: 1, pageSize: STATE_SYNC_PAGE_SIZE},
        queryKey: STATE_SYNC_QUERY_KEY,
      }),
    notifyOnChangeProps: ['data'],
    staleTime: 10_000,
    refetchInterval: 20_000,
    enabled,
  })

  const cachedTasks = useMemo<BaseTask[]>(() => data?.[0] ?? [], [data])

  const pullRequestIds = useMemo(() => getSortedResourceIds(cachedTasks), [cachedTasks])
  const {data: pullRequestsMap} = useQuery<IdToPullRequestMap>({
    queryKey: ['agent-sessions-pull-requests', pullRequestIds],
    queryFn: () => ({}),
    enabled: false,
    notifyOnChangeProps: ['data'],
  })

  const repoIds = useMemo(() => {
    if (!enabled) return []
    const ids: number[] = []
    for (const task of cachedTasks) {
      if (!task.repo_id || task.nwo || resolvePull(task, pullRequestsMap)?.repository_nwo) continue
      ids.push(task.repo_id)
    }
    return ids
  }, [enabled, cachedTasks, pullRequestsMap])
  const {data: repoMap} = useRepoInfo(repoIds)

  return useMemo(() => {
    if (!enabled || cachedTasks.length === 0) return EMPTY_LOOKUP
    const map = new Map<string, TaskStateEntry>()
    const origin = ssrSafeLocation.origin
    for (const task of cachedTasks) {
      const pull = resolvePull(task, pullRequestsMap)
      const repo = task.repo_id ? repoMap[task.repo_id] : undefined
      const nwo = pull?.repository_nwo ?? task.nwo ?? (repo ? `${repo.ownerLogin}/${repo.name}` : undefined)
      const entry: TaskStateEntry = {state: task.latest_session_state, title: pull?.title}
      for (const key of taskUrlKeys(task, nwo, origin)) {
        map.set(key, entry)
      }
    }
    return map
  }, [enabled, cachedTasks, pullRequestsMap, repoMap])
}
